import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface AuthContextType {
    token: string | null;
    userEmail: string | null;
    isLoading: boolean;
    login: (token: string, email: string) => void;
    logout: () => void;
    isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth deve ser usado dentro de AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [token, setToken] = useState<string | null>(null);
    const [userEmail, setUserEmail] = useState<string | null>(null); // ← DEFINIÇÃO CORRETA AQUI
    const [isLoading, setIsLoading] = useState(true);

    // Carregar estado inicial do localStorage
    useEffect(() => {
        const loadAuthData = () => {
            const storedToken = localStorage.getItem('token');
            const storedEmail = localStorage.getItem('userEmail');

            // console.log("🔍 AuthProvider - Carregando dados:");
            // console.log("Token no localStorage:", storedToken);
            // console.log("Email no localStorage:", storedEmail);

            setToken(storedToken);
            setUserEmail(storedEmail); // ← USANDO setUserEmail definido acima
            setIsLoading(false);
        };

        loadAuthData();

        // Escutar eventos de storage de outras abas
        const handleStorageChange = (e: StorageEvent) => {
            console.log("🔄 AuthProvider - Storage event:", e.key, e.newValue);

            if (e.key === 'token') {
                setToken(e.newValue);
            }
            if (e.key === 'userEmail') {
                setUserEmail(e.newValue); // ← USANDO setUserEmail
            }
        };

        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    const login = (newToken: string, email: string) => {
        console.log("🔑 AuthProvider - Login sendo chamado:", { newToken, email });

        // Salvar no localStorage
        localStorage.setItem('token', newToken);
        localStorage.setItem('userEmail', email);

        // Atualizar estado
        setToken(newToken);
        setUserEmail(email); // ← USANDO setUserEmail

        console.log("✅ AuthProvider - Estado atualizado");

        // Disparar evento para sincronizar entre componentes
        window.dispatchEvent(new Event('authStateChanged'));
    };

    const logout = () => {
        console.log("🚪 AuthProvider - Logout sendo chamado");

        // Remover do localStorage
        localStorage.removeItem('token');
        localStorage.removeItem('userEmail');

        // Atualizar estado
        setToken(null);
        setUserEmail(null); // ← USANDO setUserEmail

        // Disparar evento
        window.dispatchEvent(new Event('authStateChanged'));

        console.log("⚠️ AuthProvider - Estado limpo");
    };

    const value: AuthContextType = {
        token,
        userEmail,
        isLoading,
        login,
        logout,
        isAuthenticated: !!token,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};